import "../../../css/ClassificationAdmin.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Spin, Select, message } from "antd";
import { getTopics } from "../../../services/polly/classificationService";
import TopicTree from "./TopicTree";
import TopicDetail from "./TopicDetail";
import InsightsTab from "./InsightsTab";

const { Option } = Select;

const PERIOD_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
];

function findTopic(topics, id) {
  for (const t of topics) {
    if (t.id === id) return t;
    if (t.children) {
      const found = findTopic(t.children, id);
      if (found) return found;
    }
  }
  return null;
}

function flattenActiveTopics(topics, result = []) {
  for (const t of topics) {
    if (!t.disabled) result.push(t);
    if (t.children) flattenActiveTopics(t.children, result);
  }
  return result;
}

export default function ClassificationAdmin() {
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('this_month');
  const [activeTab, setActiveTab] = useState('insights');
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTopics(period);
      if (!mountedRef.current) return;
      const topicList = Array.isArray(res) ? res : (res.topics || []);
      setTopics(topicList);
      if (topicList.length > 0 && !selectedTopicId) {
        setSelectedTopicId(topicList[0].id);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn("Failed to fetch topics:", err);
      message.error("Failed to load topics");
    }
    if (mountedRef.current) setLoading(false);
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const selectedTopic = selectedTopicId ? findTopic(topics, selectedTopicId) : null;
  const flatTopics = flattenActiveTopics(topics);

  return (
    <div className="ca-page">
      <div className="ca-breadcrumb">Polly &rsaquo; <span>Classifications</span></div>
      <div className="ca-top-bar">
        <div className="ca-tabs-row">
          <div
            className={`ca-tab${activeTab === 'insights' ? ' active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            Insights
          </div>
          <div
            className={`ca-tab${activeTab === 'manage' ? ' active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            Manage
          </div>
        </div>
        <div className="ca-top-bar-right">
          <span className="ca-period-label">Period:</span>
          <Select value={period} onChange={setPeriod} size="small" className="ca-period-select">
            {PERIOD_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        </div>
      </div>

      {activeTab === 'manage' && (
        <Spin spinning={loading}>
          <div className="classification-admin">
            <TopicTree
              topics={topics}
              selectedTopicId={selectedTopicId}
              onSelect={setSelectedTopicId}
              onTopicUpdate={fetchTopics}
            />
            <div className="topic-detail-panel">
              {selectedTopic ? (
                <TopicDetail
                  topic={selectedTopic}
                  flatTopics={flatTopics}
                  allTopics={topics}
                  period={period}
                  onTopicUpdate={fetchTopics}
                />
              ) : (
                !loading && (
                  <div style={{ padding: 40, color: '#888', textAlign: 'center' }}>
                    Select a topic to view details
                  </div>
                )
              )}
            </div>
          </div>
        </Spin>
      )}

      {activeTab === 'insights' && (
        <InsightsTab topics={topics} period={period} loading={loading} />
      )}
    </div>
  );
}
